import React, { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import NavBar from "../Navigation/NavBar";
import Footer from "../Footer/Footer";
import articles from "./editorialArticles.json";
import "./EditorialArticle.css";

const ORIGIN = "https://quarec.com";

function formatDate(dateValue) {
  const date = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function EditorialArticle() {
  const { pathname } = useLocation();
  const article = articles[pathname];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  if (!article) return <Navigate to="/Blog" replace />;

  const canonical = `${ORIGIN}${pathname}`;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    dateModified: article.updatedAt,
    mainEntityOfPage: canonical,
    author: {
      "@type": "Organization",
      name: "Quarec Resources",
      url: ORIGIN,
    },
    publisher: {
      "@type": "Organization",
      name: "Quarec Resources Pvt. Ltd.",
      url: ORIGIN,
    },
  };

  return (
    <div className="qr-editorial-page">
      <Helmet>
        <meta name="author" content="Quarec Resources" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <NavBar />
      <main className="qr-editorial-main">
        <div className="qr-editorial-container">
          <nav className="qr-editorial-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/Blog">Guides</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">{article.title}</span>
          </nav>

          <article className="qr-editorial-article">
            <header className="qr-editorial-header">
              <span className="qr-editorial-category">{article.category}</span>
              <h1>{article.title}</h1>
              <p className="qr-editorial-meta">
                <time dateTime={article.updatedAt}>Updated {formatDate(article.updatedAt)}</time>
                <span aria-hidden="true">·</span>
                <span>Published by Quarec Resources</span>
              </p>
              <p className="qr-editorial-intro">{article.intro}</p>
            </header>

            <section className="qr-editorial-takeaways" aria-labelledby="qr-takeaways-heading">
              <h2 id="qr-takeaways-heading">Key takeaways</h2>
              <ul>
                {article.takeaways.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>

            <div className="qr-editorial-body">
              {article.sections.map((section) => (
                <section className="qr-editorial-section" key={section.heading}>
                  <h2>{section.heading}</h2>
                  {(section.paragraphs || []).map((paragraph, index) => (
                    <p key={`${section.heading}-p-${index}`}>{paragraph}</p>
                  ))}
                  {section.bullets?.length > 0 && (
                    <ul>
                      {section.bullets.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  )}
                  {section.steps?.length > 0 && (
                    <ol>
                      {section.steps.map((item) => <li key={item}>{item}</li>)}
                    </ol>
                  )}
                  {section.exampleTitle && (
                    <aside className="qr-editorial-example">
                      <h3>{section.exampleTitle}</h3>
                      <p>{section.exampleText}</p>
                    </aside>
                  )}
                </section>
              ))}
            </div>

            {article.sources?.length > 0 && (
              <section className="qr-editorial-sources" aria-labelledby="qr-sources-heading">
                <h2 id="qr-sources-heading">Further reading</h2>
                <p>Use these references for additional context. Where a source is specific to one country, check the rules that apply to your own location.</p>
                <ul>
                  {article.sources.map((source) => (
                    <li key={source.url}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        {source.label}
                      </a>
                      {source.note && <span> — {source.note}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <aside className="qr-editorial-disclaimer">
              This guide provides general information, not legal, employment or immigration advice. Requirements vary by role, location and circumstances; verify current rules through relevant official sources.
            </aside>

            <footer className="qr-editorial-cta">
              <h2>Put the guide into practice</h2>
              <p>Use a free worksheet or explore the recruitment services that match your needs.</p>
              <div className="qr-editorial-cta-links">
                <Link to="/Resources">Explore free resources</Link>
                <Link to="/Services">View service guides</Link>
                <Link to="/ContactUs">Contact Quarec Resources</Link>
              </div>
            </footer>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
}
